import { useCallback, useState } from "react"

import { useIsMobile } from "@/hooks/use-mobile"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog"
import {
  Drawer,
  DrawerContent,
  DrawerDescription,
  DrawerHeader,
  DrawerTitle,
  DrawerTrigger,
} from "@/components/ui/drawer"
import { VisuallyHidden } from "@radix-ui/react-visually-hidden"
import { cn } from "@/lib/utils"

type ResponsiveDialogProps = {
  children: React.ReactNode,
  trigger?: React.ReactNode,
  title: string,
  description: string,
  open?: boolean,
  onOpenChange?: (open: boolean) => void,
  hideHeader?: boolean,
  dialogClassName?: string,
  drawerClassName?: string
}

export const ResponsiveDialog = ({
  children,
  trigger,
  title,
  description,
  open,
  onOpenChange = () => {},
  hideHeader = false,
  dialogClassName = "",
  drawerClassName = ""
}: ResponsiveDialogProps) => {
  const [internalOpen, setInternalOpen] = useState(open || false)
  const isDesktop = !useIsMobile()

  const handleOpenChange = useCallback((open: boolean) => {
    setInternalOpen(open)
    onOpenChange(open)
  }, [onOpenChange])

  if (isDesktop) {
    return (
      <Dialog open={internalOpen} onOpenChange={handleOpenChange}>
        { trigger && (
          <DialogTrigger asChild>
            { trigger }
          </DialogTrigger>
        )}
        <DialogContent className={dialogClassName}>
          { hideHeader ? (
            <VisuallyHidden>
              <DialogHeader>
                <DialogTitle>{ title }</DialogTitle>
                { description && (
              <DialogDescription>
                { description }
              </DialogDescription>
            )}
              </DialogHeader>
            </VisuallyHidden>
          ) : (
            <DialogHeader>
              <DialogTitle>{ title }</DialogTitle>
              { description && (
                <DialogDescription>
                  { description }
                </DialogDescription>
              )}
            </DialogHeader>
          )}
          { children }
        </DialogContent>
      </Dialog>
    )
  }

  return (
    <Drawer open={internalOpen} onOpenChange={handleOpenChange}>
      { trigger && (
        <DrawerTrigger asChild>
          { trigger }
        </DrawerTrigger>
      )}
      <DrawerContent className={cn("h-screen", drawerClassName)}>
        { hideHeader ? (
          <VisuallyHidden>
            <DrawerHeader className="text-left">
              <DrawerTitle>{ title }</DrawerTitle>
              <DrawerDescription>
                { description }
              </DrawerDescription>
            </DrawerHeader>
          </VisuallyHidden>
        ) : (
        <DrawerHeader className="text-left">
          <DrawerTitle>{ title }</DrawerTitle>
          <DrawerDescription>
            { description }
          </DrawerDescription>
          </DrawerHeader>
        )}
        { children }
      </DrawerContent>
    </Drawer>
  )
}
